import { Box, Divider, Stack, Text } from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';

import { CurrentDraftProvider } from '../../context/CurrentDraftContext';
import { useCurrentDraft } from '../../hooks/useCurrentDraft';
import { BookingConfirmationSection } from './BookingConfirmation';
import { DraftActionBar } from './DraftActionBar';
import { UnsupportedRouteNotice } from './DraftAlerts';
import { DraftHeader } from './DraftHeader';
import { SourceEmailCard } from './SourceEmailCard';

type Props = {
  draftId: number | null;
  onResolved: () => void;
};

/**
 * Layout of the review pane; all state lives in CurrentDraftContext. BookingConfirmation is the one route with a
 * dedicated view; every other route renders read-only (source email only). Nothing is applied from here yet. A new
 * route plugs in as one more branch below plus its own section component.
 */
const DraftDetailBody = () => {
  const { draft, isPending, isBookingConfirmation, isUnsupportedRoute } = useCurrentDraft();

  return (
    <Stack h="full" spacing="0">
      <DraftHeader />

      <Box flex="1" overflowY="auto" px="5" py="4">
        <Stack spacing="4">
          <SourceEmailCard />
          <Divider />

          <Show if={isUnsupportedRoute}>
            <UnsupportedRouteNotice />
          </Show>
          <Show if={isBookingConfirmation && !!draft}>
            <BookingConfirmationSection />
          </Show>
        </Stack>
      </Box>

      {/* Sticky action bar — resolve actions must never require scrolling. */}
      <Show if={isPending}>
        <DraftActionBar key={draft?.id} />
      </Show>
    </Stack>
  );
};

/**
 * Draft review pane. Renders beside the inbox list rather than in a drawer, so the reviewer keeps their place in the
 * queue and the approve action is always one click away (see the sticky DraftActionBar).
 */
export const RequestDraftDetail = ({ draftId, onResolved }: Props) => {
  if (draftId === null) {
    return (
      <Stack h="full" align="center" justify="center" p="8">
        <Text color="gray.500">Select a draft to review it here.</Text>
      </Stack>
    );
  }

  return (
    <CurrentDraftProvider draftId={draftId} onResolved={onResolved}>
      <DraftDetailBody />
    </CurrentDraftProvider>
  );
};
